/**
 * A member's ribbon rack (docs/Decisions.md D42): ribbons in rows, then
 * badges. Every award across every organization, linked to the platform-wide
 * user; the server has already filtered it to what the viewer may see and put
 * it in order (platform precedence and award date, or the org's layout and the
 * member's arrangement where the org has the premium ribbon-designer).
 *
 *   <RibbonRack userId="me" />          the caller's own rack
 *   <RibbonRack userId={member.userId} /> another member's rack
 *   <RibbonRack rack={data} />          render data already fetched
 */
import { useEffect, useId, useState } from 'react';
import { ui, cn } from '../../lib/ui';
import { rackRows, ribbonLabel, scopeLabel } from '../../lib/ribbons';
import { getRack } from '../../api/ribbons';
import RibbonBar from './RibbonBar';
import BadgeMedallion from './BadgeMedallion';

export function RackDisplay({ rack, showScope = false }) {
  const ribbons = rack.ribbons || [];
  const badges = rack.badges || [];
  const layout = rack.layout || { ribbonsPerRow: 4, showLevels: true };
  const rows = rackRows(ribbons, layout.ribbonsPerRow);

  if (!ribbons.length && !badges.length) {
    return (
      <p className={cn(ui.muted, 'text-body-s')}>
        No ribbons yet. Post in the forum, join events and watch videos to earn the first ones.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {rows.length > 0 && (
        <ul className="flex flex-col items-center gap-1" aria-label="Ribbons">
          {rows.map((row, rowIndex) => (
            <li key={rowIndex}>
              <ul className="flex justify-center gap-1">
                {row.map(item => (
                  <li key={item.awardId}>
                    <RibbonBar
                      item={item}
                      showLevel={layout.showLevels !== false}
                      showScope={showScope}
                    />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {badges.length > 0 && (
        <ul className="flex flex-wrap justify-center gap-3" aria-label="Badges">
          {badges.map(item => (
            <li key={item.awardId} className="flex w-24 flex-col items-center gap-1 text-center">
              <BadgeMedallion
                item={item}
                showLevel={layout.showLevels !== false}
                showScope={showScope}
              />
              <span className="text-caption text-dcon-ink-muted">{ribbonLabel(item)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RackDetails({ rack }) {
  const items = [...(rack.ribbons || []), ...(rack.badges || [])];
  if (!items.length) return null;
  return (
    <details className="mt-4">
      <summary className="cursor-pointer text-body-s font-semibold text-dcon-brand">
        Ribbon and badge details
      </summary>
      <ul className="mt-2 flex flex-col gap-2">
        {items.map(item => (
          <li key={item.awardId} className="flex flex-wrap items-center gap-2 text-body-s">
            <span className="font-semibold text-dcon-ink">{ribbonLabel(item)}</span>
            <span
              className={
                item.visibility && item.visibility.scope !== 'platform'
                  ? ui.pillInfo
                  : ui.pillNeutral
              }
            >
              {scopeLabel(item.visibility)}
            </span>
            {item.hidden && <span className={ui.pillWarning}>Hidden here</span>}
            {item.description && <span className={ui.muted}>{item.description}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}

export default function RibbonRack({
  userId = 'me',
  rack: given = null,
  title = 'Ribbon rack',
  className,
}) {
  const headingId = useId();
  const [rack, setRack] = useState(given);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (given) {
      setRack(given);
      return undefined;
    }
    let cancelled = false;
    setError(null);
    getRack(userId)
      .then(data => {
        if (!cancelled) setRack(data);
      })
      .catch(err => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, given]);

  return (
    <section
      className={cn(ui.card, className)}
      aria-labelledby={headingId}
      data-testid="ribbon-rack"
    >
      <h3 id={headingId} className={ui.h3}>
        {title}
      </h3>
      {error ? (
        <p className={cn(ui.muted, 'text-body-s')}>
          Couldn&rsquo;t load the ribbon rack. Reload the page to try again.
        </p>
      ) : !rack ? (
        <p className={ui.muted} role="status">
          Loading ribbons
        </p>
      ) : (
        <>
          <RackDisplay rack={rack} showScope={Boolean(rack.isOwner)} />
          {rack.isOwner && <RackDetails rack={rack} />}
        </>
      )}
    </section>
  );
}
